import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const SampleMessage = ({ children }) => (
  <Box
    sx={{
      bgcolor: '#f5f5f5',
      borderLeft: '3px solid #999',
      px: 2,
      py: 1.5,
      my: 1,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
    }}
  >
    {children}
  </Box>
);

// Visual preview of the /profile Phone Number section — non-interactive,
// rendered with the same MUI components for visual fidelity to what users
// actually see after signing in.
const ProfilePreview = () => (
  <Paper elevation={2} sx={{ p: 3, mb: 2, maxWidth: 600, mx: 'auto' }}>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      Phone Number
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Add your phone number to receive SMS notifications about game updates.
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="+1 (801) 555-1234"
        disabled
        value=""
      />
      <Button variant="outlined" size="small" disabled>
        Verify
      </Button>
    </Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}
    >
      By clicking Verify, you consent to receive a one-time SMS verification code at this number
      from The Big Board. After verification, you can choose which notifications to enable
      (new-week game alerts, pick reminders, and leaderboard updates). Message frequency varies.
      Message and data rates may apply. Reply STOP to opt out or HELP for help.{' '}
      <RouterLink to="/terms" style={{ color: '#666', fontSize: 'inherit' }}>
        Terms of Service
      </RouterLink>
      {' | '}
      <RouterLink to="/privacy" style={{ color: '#666', fontSize: 'inherit' }}>
        Privacy Policy
      </RouterLink>
    </Typography>
    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        SMS Notifications (shown after phone is verified)
      </Typography>
      <FormControlLabel
        control={<Switch checked={false} disabled />}
        label="New games published"
      />
      <br />
      <FormControlLabel
        control={<Switch checked={false} disabled />}
        label="Picks reminder (2hrs before kickoff)"
      />
      <br />
      <FormControlLabel
        control={<Switch checked={false} disabled />}
        label="Leaderboard updates"
      />
    </Box>
  </Paper>
);

export default function SmsProgram() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container
      maxWidth="md"
      sx={{ mt: isMobile ? '80px' : '128px', mb: 6, px: isMobile ? 2 : 3 }}
    >
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        BigBoard SMS Notification Program
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Public information page — no sign-in required. Last updated: May 13, 2026.
      </Typography>

      <Section title="Overview">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The Big Board (bigboard.football) is a weekly college football pick prediction app.
          Our SMS Notification Program is an entirely optional feature that lets registered users
          receive game-related alerts via text message. Users opt in through a verified phone-number
          flow on their profile page after creating an account. SMS notifications are never required
          to use the Service, and all notification toggles default to OFF.
        </Typography>
      </Section>

      <Section title="How users consent (Call to Action)">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The complete consent flow is a multi-step process. The full consent disclosure text and
          notification controls are rendered on each user's <strong>/profile</strong> page after
          they sign in. Below is a faithful visual preview of what users see, rendered with the
          same components used in the live application:
        </Typography>
        <Box sx={{ my: 3 }}>
          <ProfilePreview />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
          >
            Preview of the /profile Phone Number section. All controls disabled for display purposes.
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
          Step-by-step:
        </Typography>
        <Box component="ol" sx={{ mt: 1, pl: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
          <li>
            User creates an account at{' '}
            <a href="https://bigboard.football/signup" style={{ color: '#555' }}>
              bigboard.football/signup
            </a>{' '}
            with email + password.
          </li>
          <li>
            User navigates to their profile at{' '}
            <a href="https://bigboard.football/profile" style={{ color: '#555' }}>
              bigboard.football/profile
            </a>
            .
          </li>
          <li>
            User enters their phone number into the Phone Number field. A consent disclosure
            appears below the field (shown above) explicitly informing them that clicking Verify
            constitutes consent to receive a one-time SMS verification code, with links to the
            Terms of Service and Privacy Policy.
          </li>
          <li>
            User clicks <strong>Verify</strong>. Our server sends a 6-digit one-time passcode to
            the entered phone number.
          </li>
          <li>
            User receives the OTP via SMS and enters it into the verification dialog. On successful
            verification, their phone number is marked verified in our system.
          </li>
          <li>
            User can now toggle individual notification preferences ON. All toggles default to OFF —
            no marketing messages are sent without explicit per-channel opt-in.
          </li>
          <li>
            User may reply <strong>STOP</strong> to any message at any time to opt out of all SMS,
            or disable specific notification toggles in their profile.
          </li>
        </Box>
      </Section>

      <Section title="Message types and samples">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The program sends exactly four message types. All include "Msg&data rates may apply" and
          "Reply STOP to opt out" disclosures.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
          1. Phone verification one-time code (transactional)
        </Typography>
        <SampleMessage>
          BigBoard: Your verification code is 482917. Msg&Data Rates May Apply. Reply STOP to opt out.
        </SampleMessage>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
          2. New games published alert
        </Typography>
        <SampleMessage>
          BigBoard: Week 9 games are live! Submit your picks at https://bigboard.football. Reply STOP to opt out. Msg&data rates may apply.
        </SampleMessage>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
          3. Kickoff reminder
        </Typography>
        <SampleMessage>
          BigBoard: Kickoff in 2 hours and you still have unsubmitted picks for Week 9 on Saturday. Submit at https://bigboard.football. Reply STOP to opt out. Msg&data rates may apply.
        </SampleMessage>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Sent automatically ~2 hours before the first kickoff of each game day,
          only to users with at least one unsubmitted pick for that day. At most
          one reminder per user per game day.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
          4. Leaderboard update
        </Typography>
        <SampleMessage>
          BigBoard: Week 8 results are in. See the updated leaderboard at https://bigboard.football/leaderboard. Reply STOP to opt out. Msg&data rates may apply.
        </SampleMessage>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Sent at most once per week, when final scores for that week are published.
        </Typography>
      </Section>

      <Section title="Keywords">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          <strong>Opt-out (any of):</strong> STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 1 }}>
          Replying STOP both blocks all future SMS at the carrier level and
          turns OFF every notification preference toggle on the user's profile.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
          <strong>Opt-in / resubscribe (any of):</strong> START, UNSTOP, YES
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 1 }}>
          Replying START lifts the carrier-level block, but does NOT re-enable
          the notification toggles. To actually start receiving notifications
          again, the user must visit /profile and toggle the desired notification
          types back ON. This is intentional and CTIA-compliant: STOP is a clean
          slate; users must explicitly opt back in to each notification type.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
          <strong>Help:</strong> HELP, INFO — replies with:
        </Typography>
        <SampleMessage>
          BigBoard: For support, email support@bigboard.football. Reply STOP to opt out. Msg&data rates may apply.
        </SampleMessage>
      </Section>

      <Section title="Frequency and rates">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          Message frequency varies based on each user's notification preferences and the
          college-football schedule (typically 1-4 messages per week during the season per
          enabled notification type, plus the one-time verification code on signup). Message and
          data rates may apply depending on your mobile carrier plan. Carriers are not liable for
          delayed or undelivered messages.
        </Typography>
      </Section>

      <Section title="Privacy">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We do not sell, share, or rent your phone number to third parties.{' '}
          <strong>
            No mobile information or SMS opt-in consent will be shared with third parties or
            affiliates for marketing or promotional purposes.
          </strong>{' '}
          Phone numbers are stored securely and used solely for sending notifications you have
          explicitly opted into. See the full{' '}
          <RouterLink to="/privacy" style={{ color: '#555' }}>
            Privacy Policy
          </RouterLink>{' '}
          for complete details.
        </Typography>
      </Section>

      <Section title="Support">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          For questions about the SMS program, opt-out issues, or any other support need, contact
          us at{' '}
          <a href="mailto:support@bigboard.football" style={{ color: '#555' }}>
            support@bigboard.football
          </a>
          .
        </Typography>
      </Section>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        See also our{' '}
        <RouterLink to="/terms" style={{ color: '#666' }}>
          Terms of Service
        </RouterLink>{' '}
        and{' '}
        <RouterLink to="/privacy" style={{ color: '#666' }}>
          Privacy Policy
        </RouterLink>
        .
      </Typography>
    </Container>
  );
}
